/* eslint-disable react/require-default-props */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { FC } from "react";
import { Box, Divider, Tooltip, Typography } from "@material-ui/core";
import { DsmIcon } from "@dsm-dcs/design-system-react";
import clsx from "clsx";
import { useIntl } from "../../../../../_metronic/i18n/customUseIntl";
import { roiResultsStyles } from "../../../../ThemeProvider";
import PercentageBadge from "./PercentageBadge";

interface ComparisonBarCardProps {
  title: string;

  description?: string;

  tooltip?: string;
  baseline: number;
  intervention: number;

  formatValue: (value: number) => string;

  valueUnit?: string;
  percentageChange: number;

  available?: boolean;
}

const MIN_BAR_HEIGHT_PCT = 4;

const BARS_ROW_HEIGHT_PX = 270;
const BAR_LABEL_HEIGHT_PX = 32;
const BAR_TRACK_HEIGHT_PX = BARS_ROW_HEIGHT_PX - BAR_LABEL_HEIGHT_PX;

const VALUE_INSIDE_MIN_PCT = 30;

const ComparisonBarCard: FC<ComparisonBarCardProps> = ({
  title,
  description,
  tooltip,
  baseline,
  intervention,
  formatValue,
  valueUnit,
  percentageChange,
  available = true,
}) => {
  const intl = useIntl();
  const classes = roiResultsStyles();

  const max = Math.max(baseline, intervention, 0);

  const heightPctOf = (value: number): number =>
    max > 0
      ? Math.max((value / max) * 100, MIN_BAR_HEIGHT_PCT)
      : MIN_BAR_HEIGHT_PCT;

  const renderBar = (value: number, side: "baseline" | "intervention") => {
    const heightPct = heightPctOf(value);
    const valueFitsInside = heightPct >= VALUE_INSIDE_MIN_PCT;
    const label = intl.formatMessage({
      id:
        side === "baseline"
          ? "SUSTELL.ROI.RESULTS.BASELINE"
          : "SUSTELL.ROI.RESULTS.INTERVENTION",
    });

    return (
      <Box className={classes.barColumn}>
        {!valueFitsInside && (
          <>
            <Typography
              className={clsx(classes.barValue, classes.barValueOutside)}
            >
              {formatValue(value)}
            </Typography>
            {valueUnit && (
              <Typography
                className={classes.barValueUnit}
                style={{ color: "#1B1B1B" }}
              >
                {valueUnit}
              </Typography>
            )}
          </>
        )}

        <Box
          className={clsx(
            classes.bar,
            side === "baseline" ? classes.barBaseline : classes.barIntervention,
          )}
          style={{
            height: `${Math.round((heightPct / 100) * BAR_TRACK_HEIGHT_PX)}px`,
          }}
        >
          <Typography
            className={clsx(
              classes.barLabel,
              side === "baseline"
                ? classes.barLabelBaseline
                : classes.barLabelIntervention,
            )}
          >
            {label}
          </Typography>

          {valueFitsInside && (
            <>
              <Typography className={classes.barValue}>
                {formatValue(value)}
              </Typography>
              {valueUnit && (
                <Typography className={classes.barValueUnit}>
                  {valueUnit}
                </Typography>
              )}
            </>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box className={classes.chartCard}>
      <Box className={classes.chartTitleRow}>
        <Typography className={classes.chartTitle}>{title}</Typography>

        {tooltip && (
          <Tooltip title={tooltip} placement="top" arrow>
            <DsmIcon
              name="general/help-circle"
              className={classes.metricHelpIcon}
            />
          </Tooltip>
        )}
      </Box>

      {description && (
        <Typography className={classes.chartDescription}>
          {description}
        </Typography>
      )}

      {available && (
        <>
          <Box className={classes.barsRow}>
            {renderBar(baseline, "baseline")}
            {renderBar(intervention, "intervention")}
          </Box>

          <Divider className={classes.barsFooterDivider} />

          <Box className={classes.percentageRow}>
            <Typography className={classes.percentageLabel}>
              {intl.formatMessage({
                id: "SUSTELL.ROI.RESULTS.PERCENTAGE_CHANGE",
              })}
            </Typography>
            <PercentageBadge value={percentageChange} />
          </Box>
        </>
      )}
    </Box>
  );
};

export default ComparisonBarCard;
